import { useEffect, useState } from "react";
import { api, coincide, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Send, Users, Copy, Search } from "lucide-react";

const TIPOS = [
  { key: "mensual", label: "Suscripción mensual" },
  { key: "trimestral", label: "Suscripción trimestral" },
  { key: "semestral", label: "Suscripción semestral" },
  { key: "anual", label: "Suscripción anual" },
  { key: "unico", label: "Pago único (clase suelta o bono)" },
  { key: "verano", label: "Curso de verano (pago único)" },
];

const PUBLICOS = [
  { key: "todos", label: "Todas las familias" },
  { key: "nuevas", label: "Familias nuevas" },
  { key: "continuidad", label: "Bono de continuidad (ya estuvieron el curso pasado)" },
  { key: "hermanos2", label: "Familias con 2 o más hijos" },
  { key: "hermanos3", label: "Familias con 3 o más hijos" },
  { key: "inactivas", label: "Familias inactivas (vuelta al centro)" },
];

const CATEGORIAS = ["SUSCRIPCIONES", "BONOS", "EXTRAS", "OPEN DAY"];

const empty = { sede_id: "", nombre: "", tipo: "mensual", importe: 0, descripcion: "", hermanos: 1, dias_semana: 1, publico: "todos", categoria: "", clases_incluidas: 0, validez_meses: 0, activo: true, matricula: false, matricula_importe: 0, seguro: false, seguro_importe: 0, block: false, block_importe: 15, campamento_semanas: 0 };

export default function Planes() {
  const [items, setItems] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [padres, setPadres] = useState([]);
  const [enviar, setEnviar] = useState(null);
  const [padreSel, setPadreSel] = useState("");
  const [filtroSede, setFiltroSede] = useState("todas");
  const [filtroCategoria, setFiltroCategoria] = useState("todas");
  const [filtroVisible, setFiltroVisible] = useState("todos");
  const [filtroHijos, setFiltroHijos] = useState("todos");
  const [q, setQ] = useState("");
  const [busqueda, setBusqueda] = useState("");

  const enviarPlan = async () => {
    if (!padreSel) return toast.error("Elige la familia");
    try {
      const { data } = await api.post(`/planes/${enviar.id}/enviar`, { padre_id: padreSel });
      toast.success(data.email_enviado
        ? `Plan enviado a ${data.familia} por email`
        : `Plan asignado a ${data.familia} (el email no se pudo enviar)`);
      setEnviar(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const publicar = async (id) => {
    await api.post(`/planes/${id}/publicar`);
    toast.success("El plan vuelve a estar visible para todas las familias");
    load();
  };

  const load = () => api.get("/planes").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
    api.get("/sedes").then(({ data }) => setSedes(data));
    api.get("/padres").then(({ data }) => setPadres(data));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const sedeName = (id) => sedes.find((s) => s.id === id)?.nombre || "—";

  const save = async () => {
    if (!form.sede_id) return toast.error("Selecciona una sede");
    setSaving(true);
    try {
      const payload = { ...form, importe: Number(form.importe) || 0, hermanos: Number(form.hermanos) || 1,
        matricula_importe: Number(form.matricula_importe) || 0,
        clases_incluidas: Number(form.clases_incluidas) || 0,
        campamento_semanas: Number(form.campamento_semanas) || 0,
        validez_meses: Number(form.validez_meses) || 0,
        seguro_importe: Number(form.seguro_importe) || 0,
        block: !!form.block,
        block_importe: Number(form.block_importe) || 0 };
      if (editId) await api.put(`/planes/${editId}`, payload);
      else await api.post("/planes", payload);
      toast.success(editId ? "Plan actualizado" : "Plan creado en Stripe");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/planes/${id}`);
    toast.success("Plan desactivado");
    load();
  };

  const filtrados = items.filter((p) =>
    (filtroSede === "todas" || p.sede_id === filtroSede)
    && (filtroCategoria === "todas"
      || (filtroCategoria === "sin" ? !p.categoria : p.categoria === filtroCategoria))
    && (filtroVisible === "todos" || (filtroVisible === "visibles" ? p.activo : !p.activo))
    && (filtroHijos === "todos" || String(p.hermanos || 1) === filtroHijos)
    && coincide([p.nombre, p.descripcion, p.categoria, p.tipo, sedeName(p.sede_id),
                 String(p.importe)].join(" "), q));

  return (
    <div data-testid="planes-page">
      <PageHeader
        title="Planes de pago"
        subtitle="Precios que las familias verán en el portal. Se crean automáticamente en Stripe."
        action={
          <Button className="rounded-full font-semibold" data-testid="add-plan-btn"
            onClick={() => { setForm(empty); setEditId(null); setOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" /> Nuevo plan
          </Button>
        }
      />
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9"
            placeholder="Buscar plan por nombre, precio, sede…" data-testid="buscar-plan-input" />
        </div>
        <div className="w-full sm:w-56">
          <Select value={filtroSede} onValueChange={setFiltroSede}>
            <SelectTrigger data-testid="filtro-sede-planes">
              <SelectValue placeholder="Todas las sedes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas las sedes</SelectItem>
              {sedes.map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-full sm:w-52">
          <Select value={filtroCategoria} onValueChange={setFiltroCategoria}>
            <SelectTrigger data-testid="filtro-categoria-planes">
              <SelectValue placeholder="Todas las categorías" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas las categorías</SelectItem>
              {CATEGORIAS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              <SelectItem value="sin">Sin categoría</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-full sm:w-52">
          <Select value={filtroVisible} onValueChange={setFiltroVisible}>
            <SelectTrigger data-testid="filtro-visible-planes"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Visibles y ocultos</SelectItem>
              <SelectItem value="visibles">Sólo visibles</SelectItem>
              <SelectItem value="ocultos">Sólo ocultos</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-full sm:w-52">
          <Select value={filtroHijos} onValueChange={setFiltroHijos}>
            <SelectTrigger data-testid="filtro-hijos-planes"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Cualquier nº de hijos</SelectItem>
              <SelectItem value="1">1 hijo/a</SelectItem>
              <SelectItem value="2">2 hermanos</SelectItem>
              <SelectItem value="3">3 hermanos</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <p className="text-sm text-muted-foreground" data-testid="planes-contador">
          {filtrados.length} plan(es)
        </p>
      </div>

      <div className="space-y-10">
        {["SUSCRIPCIONES", "BONOS", "EXTRAS", "OPEN DAY", ""].map((cat) => {
          const grupo = filtrados.filter((p) => (p.categoria || "") === cat);
          if (grupo.length === 0) return null;
          return (
            <div key={cat || "sin"} data-testid={`grupo-planes-${cat || "sin-categoria"}`}>
              <h2 className="font-display text-xl font-extrabold mb-4">{cat || "Sin categoría"}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {grupo.map((p) => (
          <Card key={p.id} className="rounded-2xl p-6 border-border shadow-sm" data-testid={`plan-card-${p.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <Badge variant="outline" className="rounded-full capitalize mb-2">
                  {p.tipo === "unico" ? "Pago único" : p.tipo === "verano" ? "Curso de verano" : p.tipo}
                </Badge>
                {p.hermanos > 1 && (
                  <Badge className="rounded-full ml-2" data-testid={`plan-hermanos-${p.id}`}>{p.hermanos} hermanos</Badge>
                )}
                {p.publico && p.publico !== "todos" && (
                  <Badge variant="secondary" className="rounded-full ml-2" data-testid={`plan-publico-${p.id}`}>
                    {PUBLICOS.find((x) => x.key === p.publico)?.label || p.publico}
                  </Badge>
                )}
                {p.categoria && (
                  <Badge variant="secondary" className="rounded-full ml-2" data-testid={`plan-categoria-${p.id}`}>
                    {p.categoria}
                  </Badge>
                )}
                <h3 className="font-display font-bold text-lg leading-tight">{p.nombre}</h3>
                <p className="text-xs text-muted-foreground mt-1">{sedeName(p.sede_id)}</p>
              </div>
              <Badge variant={p.activo ? "default" : "secondary"}>{p.activo ? "Activo" : "Inactivo"}</Badge>
            </div>
            {p.descripcion && <p className="text-sm text-muted-foreground mt-3">{p.descripcion}</p>}
            <p className="font-display text-3xl font-extrabold mt-5">{p.importe} €</p>
            {(p.matricula || p.seguro || p.block || p.campamento_semanas) && (
              <div className="text-xs mt-3 space-y-1" data-testid={`plan-extras-${p.id}`}>
                {p.matricula && (
                  <p className="text-muted-foreground">
                    {p.matricula_importe > 0
                      ? `Matrícula anual: + ${p.matricula_importe} € (obligatoria en el primer pago del año)`
                      : "Matrícula anual: incluida en el precio"}
                  </p>
                )}
                {p.seguro && (
                  <p className="text-muted-foreground">
                    {p.seguro_importe > 0
                      ? `Seguro de recuperación de clases: + ${p.seguro_importe} € al año, opcional (lo marca la familia al pagar)`
                      : "Seguro de recuperación de clases: incluido en el precio"}
                  </p>
                )}
                {p.block && (
                  <p className="text-muted-foreground">
                    {p.block_importe > 0
                      ? `Block de dibujo: + ${p.block_importe} €, opcional (lo marca la familia al pagar)`
                      : "Block de dibujo: incluido en el precio"}
                  </p>
                )}
                {p.campamento_semanas > 0 && (
                  <p className="text-muted-foreground">
                    Campamento de verano: {p.campamento_semanas} semana(s) incluida(s)
                  </p>
                )}
              </div>
            )}
            {p.padre_nombre && (
              <p className="text-xs mt-3 font-semibold text-primary" data-testid={`plan-exclusivo-${p.id}`}>
                Exclusivo de {p.padre_nombre}
              </p>
            )}
            <div className="flex flex-wrap gap-2 mt-6">
              <Button variant="outline" size="sm" className="rounded-lg" data-testid={`edit-plan-${p.id}`}
                onClick={() => { setForm({ ...empty, ...p }); setEditId(p.id); setOpen(true); }}>
                <Pencil className="h-3.5 w-3.5 mr-1.5" /> Editar
              </Button>
              <Button variant="outline" size="sm" className="rounded-lg" data-testid={`duplicar-plan-${p.id}`}
                onClick={() => {
                  setForm({ ...empty, ...p, nombre: `${p.nombre} (copia)`, id: undefined });
                  setEditId(null);
                  setOpen(true);
                }}>
                <Copy className="h-3.5 w-3.5 mr-1.5" /> Duplicar
              </Button>
              <Button variant="outline" size="sm" className="rounded-lg" data-testid={`enviar-plan-${p.id}`}
                onClick={() => { setEnviar(p); setPadreSel(""); setBusqueda(""); }}>
                <Send className="h-3.5 w-3.5 mr-1.5" /> Enviar a familia
              </Button>
              {p.padre_id && (
                <Button variant="ghost" size="sm" className="rounded-lg" data-testid={`publicar-plan-${p.id}`}
                  onClick={() => publicar(p.id)}>
                  <Users className="h-3.5 w-3.5 mr-1.5" /> Hacer público
                </Button>
              )}
              <Button variant="outline" size="sm" className="rounded-lg text-destructive" data-testid={`delete-plan-${p.id}`}
                onClick={() => remove(p.id)}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </Card>
        ))}
              </div>
            </div>
          );
        })}
        {items.length === 0 && (
          <p className="text-muted-foreground text-sm">Todavía no hay planes. Crea el primero para poder cobrar online.</p>
        )}
      </div>

      <Dialog open={!!enviar} onOpenChange={(v) => !v && setEnviar(null)}>
        <DialogContent className="rounded-2xl max-h-[85vh] overflow-y-auto overscroll-contain" data-testid="enviar-plan-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Enviar plan a una familia</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            El plan <b>{enviar?.nombre}</b> pasará a ser exclusivo de esa familia (no lo verá nadie
            más) y recibirá un email con el enlace para pagarlo.
          </p>
          <div className="grid gap-2">
            <Label>Familia</Label>
            <Input value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Busca por nombre del padre o tutor, email o teléfono"
              data-testid="enviar-plan-buscar-input" />
            <div className="max-h-64 overflow-y-auto rounded-xl border border-border divide-y divide-border"
              data-testid="enviar-plan-resultados">
              {padres
                .filter((p) => coincide(`${p.nombre} ${p.apellidos} ${p.email} ${p.telefono || ""}`, busqueda))
                .slice(0, 30)
                .map((p) => (
                  <button key={p.id} onClick={() => setPadreSel(p.id)}
                    data-testid={`enviar-plan-padre-${p.id}`}
                    className={`w-full text-left px-4 py-3 text-sm transition-colors duration-200 ${
                      padreSel === p.id ? "bg-accent font-semibold" : "hover:bg-muted"}`}>
                    {p.nombre} {p.apellidos}
                    <span className="block text-xs text-muted-foreground">{p.email}</span>
                  </button>
                ))}
              {padres.filter((p) => coincide(`${p.nombre} ${p.apellidos} ${p.email}`, busqueda)).length === 0 && (
                <p className="px-4 py-3 text-sm text-muted-foreground" data-testid="enviar-plan-sin-resultados">
                  Ninguna familia coincide con la búsqueda
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button className="rounded-full font-semibold" onClick={enviarPlan}
              data-testid="confirmar-enviar-plan-btn">
              <Send className="h-4 w-4 mr-2" /> Enviar y notificar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-xl max-h-[85vh] overflow-y-auto overscroll-contain" data-testid="plan-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editId ? "Editar plan" : "Nuevo plan"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label>Nombre del plan</Label>
              <Input value={form.nombre} onChange={(e) => set("nombre", e.target.value)}
                placeholder="Clases de dibujo 1 día/semana" data-testid="plan-nombre-input" />
            </div>
            <div className="grid gap-2">
              <Label>Sede</Label>
              <Select value={form.sede_id} onValueChange={(v) => set("sede_id", v)} disabled={!!editId}>
                <SelectTrigger data-testid="plan-sede-select"><SelectValue placeholder="Selecciona sede" /></SelectTrigger>
                <SelectContent>
                  {sedes.map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label>Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => set("tipo", v)} disabled={!!editId}>
                  <SelectTrigger data-testid="plan-tipo-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPOS.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Importe total (€)</Label>
                <Input type="number" value={form.importe} onChange={(e) => set("importe", e.target.value)}
                  data-testid="plan-importe-input" />
              </div>
              <div className="grid gap-2">
                <Label>Hermanos que cubre</Label>
                <Select value={String(form.hermanos)} onValueChange={(v) => set("hermanos", Number(v))} disabled={!!editId}>
                  <SelectTrigger data-testid="plan-hermanos-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 alumno</SelectItem>
                    <SelectItem value="2">2 hermanos</SelectItem>
                    <SelectItem value="3">3 hermanos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Días por semana</Label>
              <Input type="number" min={1} max={5} value={form.dias_semana}
                onChange={(e) => set("dias_semana", Number(e.target.value) || 1)}
                data-testid="plan-dias-input" />
              <p className="text-xs text-muted-foreground">
                Turnos que incluye la cuota. La familia elegirá el día y la hora al contratarla.
              </p>
            </div>
            <div className="grid gap-2">
              <Label>Público objetivo</Label>
              <Select value={form.publico} onValueChange={(v) => set("publico", v)}>
                <SelectTrigger data-testid="plan-publico-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PUBLICOS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Sólo las familias que cumplen esta condición verán el plan en su área de familias.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label>Categoría</Label>
                <Select value={form.categoria || "sin"} onValueChange={(v) => set("categoria", v === "sin" ? "" : v)}>
                  <SelectTrigger data-testid="plan-categoria-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sin">Sin categoría</SelectItem>
                    {CATEGORIAS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Clases incluidas (bonos)</Label>
                <Input type="number" min={0} value={form.clases_incluidas}
                  onChange={(e) => set("clases_incluidas", e.target.value)}
                  data-testid="plan-clases-incluidas-input" />
              </div>
              <div className="grid gap-2">
                <Label>Meses de validez (bonos)</Label>
                <Input type="number" min={0} value={form.validez_meses}
                  onChange={(e) => set("validez_meses", e.target.value)}
                  data-testid="plan-validez-input" />
                <p className="text-xs text-muted-foreground">
                  Ejemplo: 5 clases en 3 meses. Déjalo en 0 si no es un bono.
                </p>
              </div>
              <div className="grid gap-2">
                <Label>Semanas de campamento incluidas</Label>
                <Input type="number" min={0} value={form.campamento_semanas}
                  onChange={(e) => set("campamento_semanas", e.target.value)}
                  data-testid="plan-campamento-input" />
                <p className="text-xs text-muted-foreground">
                  Ejemplo: 1 para el Open Day, que incluye una semana de campamento de verano.
                </p>
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Descripción</Label>
              <Textarea value={form.descripcion} onChange={(e) => set("descripcion", e.target.value)} data-testid="plan-descripcion-input" />
            </div>
            <div className="rounded-lg border border-border p-4 grid gap-4" data-testid="plan-extras">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                Cobros anuales adicionales (sólo la primera vez y una vez al año)
              </p>
              <div className="flex items-center justify-between gap-4">
                <Label>Cobrar matrícula (obligatoria, se suma al primer pago del año)</Label>
                <Switch checked={!!form.matricula} onCheckedChange={(v) => set("matricula", v)}
                  data-testid="plan-matricula-switch" />
              </div>
              {form.matricula && (
                <Input type="number" value={form.matricula_importe}
                  onChange={(e) => set("matricula_importe", e.target.value)}
                  placeholder="Importe de la matrícula (€)" data-testid="plan-matricula-importe" />
              )}
              <div className="flex items-center justify-between gap-4">
                <Label>Ofrecer seguro de recuperación de clases (extra opcional)</Label>
                <Switch checked={!!form.seguro} onCheckedChange={(v) => set("seguro", v)}
                  data-testid="plan-seguro-switch" />
              </div>
              {form.seguro && (
                <Input type="number" value={form.seguro_importe}
                  onChange={(e) => set("seguro_importe", e.target.value)}
                  placeholder="Importe del seguro anual (€)" data-testid="plan-seguro-importe" />
              )}
              <div className="flex items-center justify-between gap-4">
                <Label>Ofrecer block de dibujo (extra opcional)</Label>
                <Switch checked={!!form.block}
                  onCheckedChange={(v) => setForm((f) => ({
                    ...f, block: v,
                    block_importe: v && !Number(f.block_importe) ? 15 : f.block_importe }))}
                  data-testid="plan-block-switch" />
              </div>
              {form.block && (
                <Input type="number" value={form.block_importe}
                  onChange={(e) => set("block_importe", e.target.value)}
                  placeholder="Importe del block de dibujo (€)" data-testid="plan-block-importe" />
              )}
              <p className="text-xs text-muted-foreground">
                Con importe 0 € el extra queda <strong>incluido</strong> en el precio del plan y se
                concede al pagar. Con importe mayor que 0 se ofrece como extra que la familia marca
                o no (seguro y block se eligen por separado). Al cambiar un importe se crea un precio
                nuevo en Stripe: los cobros ya realizados y las suscripciones en curso no se modifican.
              </p>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <Label>Plan visible para familias</Label>
              <Switch checked={form.activo} onCheckedChange={(v) => set("activo", v)} data-testid="plan-activo-switch" />
            </div>
            {editId && (
              <p className="text-xs text-muted-foreground">
                El tipo de plan y la sede no se pueden cambiar. El importe sí: se crea un precio
                nuevo en Stripe y los cobros ya realizados no se modifican.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button className="rounded-full font-semibold" onClick={save} disabled={saving} data-testid="save-plan-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
